import { Component, OnInit, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import posthog from "posthog-js";

import { PageWrapperComponent } from '../../../components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { HeavyHeaderComponent } from '../../../components/ui-kit/typography/heavy-header/heavy-header.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { PrimaryBtnComponent } from '../../../components/ui-kit/buttons/primary-btn/primary-btn.component';
import { SecondaryBtnComponent } from 'app/components/ui-kit/buttons/secondary-btn/secondary-btn.component';

import { MembershipsService } from 'app/services/memberships.service';
import { UserService } from 'app/services/user.service';
import { AppStateService } from 'app/services/app-state.service';
import { AlertMessageComponent } from "app/components/ui-kit/alerts/alert-message/alert-message.component";
import { TextButtonComponent } from "app/components/ui-kit/buttons/text-button/text-button.component";
import { ItineraryAnimationComponent } from "app/components/distinct-components/animations/document-animations/itinerary-animation/itinerary-animation.component";
import { IconPromptCardComponent } from "app/components/distinct-components/cards/icon-prompt-card/icon-prompt-card.component";

@Component({
  selector: 'app-verify-membership',
  standalone: true,
  imports: [
    PageWrapperComponent,
    HeavyHeaderComponent,
    CommonModule,
    SpinnerComponent,
    PrimaryBtnComponent,
    AlertMessageComponent,
    TextButtonComponent,
    ItineraryAnimationComponent,
    IconPromptCardComponent,
    TextButtonComponent
  ],
  templateUrl: './verify-membership.component.html',
  styleUrl: './verify-membership.component.scss'
})
export class VerifyMembershipComponent implements OnInit {
  membershipId: string;
  isOnboarding: boolean = false;
  membership: any;

  file: File | null = null;
  isProcessing = false;
  isVerified: boolean = false;
  isSoftVerified: boolean = false;
  isNeedReview = false;
  error: string | null = null;
  result: any = null;

  readonly attemptLimit = 15;
  attemptCount = 0;
  hasReachedAttemptLimit = false;

  fullName: string = '';
  mobileNumber: string = '';
  email: string = '';
  userId: any;
  isDragOver = false;

  providerInstructions: string = '';
  isLoadingProviderData: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appState: AppStateService,
    private userService: UserService,
    private membershipsService: MembershipsService
  ) {
    // keep user profile up to date
    effect(() => {
      const profile = this.appState.user.profile();
      console.log(profile);
      if (profile) {
        this.userId = profile.id;
        this.fullName = `${profile.firstName} ${profile.lastName}`;
        this.mobileNumber = profile.mobileNumber;
        this.email = profile.email;
        if (this.membershipId) {
          // find membership when profile arrives
          const membership = (profile.memberships || []).find((m: any) => m.id === this.membershipId);
          if (membership) {
            this.membership = membership;
            this.loadMembershipContext();
            this.checkApprovalState();
            this.syncAttemptState(membership);
          }
        }
      }
    });
  }

  ngOnInit() {
    // grab the :id param
    this.membershipId = this.route.snapshot.paramMap.get('id')!;
    this.isOnboarding = this.router.url.includes('/onboard');
  }

  loadMembershipContext() {
    const provider = this.membership.provider;
    switch (provider) {
      case 'NetJets':
        this.providerInstructions = 'All you need is a PDF itinerary. Search your email for "NetJets itinerary" and download the attached PDF.';
        break;
      case 'WheelsUp':
        this.providerInstructions = 'Upload a recent flight itinerary you have received from Wheels Up. You must upload a PDF. Screenshots are not accepted.';
        break;
      case 'FlexJet':
        this.providerInstructions = 'Upload a recent flight itinerary you have received from Wheels Up. You must upload a PDF. Screenshots are not accepted.';
        break;
    }
    this.isLoadingProviderData = false;
  }

  async onFileSelected(event: Event) {
    if (event instanceof DragEvent) {
      event.preventDefault();
      this.isDragOver = false;
    }

    if (this.hasReachedAttemptLimit) {
      return;
    }

    this.isNeedReview = false;
    this.isVerified = false;
    this.isSoftVerified = false;
    this.error = null;
    let f: File | null = null;
    if (event instanceof DragEvent && event.dataTransfer) {
      f = event.dataTransfer.files?.[0] || null;
    } else {
      f = (event.target as HTMLInputElement).files?.[0] || null;
    }
    if (!f) return;
    this.file = f;
    this.isProcessing = true;

    try {
      if (!this.membership) {
        throw new Error('Membership information not found.');
      }

      this.result = await this.membershipsService.submitMembershipDocument({
        file: this.file,
        fullName: this.fullName,
        userId: this.userId,
        membershipId: this.membershipId,
        mobileNumber: this.mobileNumber,
        provider: this.membership?.provider,
        membershipData: this.membership,
        email: this.email,
      });

      const { status, flags, matchedSignals, matchedCount, nameMatch, phoneMatch } = this.result;

      if (status === 'failed') {
        this.isVerified = false;
        this.isNeedReview = false;
        this.error = 'Document verification failed.';
        await this.updateMembershipInProfile({
          statementVerified: false,
          statementAccessGranted: false,
          statementFlags: flags,
          statementMatchedSignals: matchedSignals,
          statementMatchedCount: matchedCount,
          statementNameMatch: nameMatch,
          statementPhoneMatch: phoneMatch,
          statementStatus: status,
        }, { incrementAttemptCount: true });
        return;
      }

      if (status === 'review') {
        this.isVerified = false;
        this.isSoftVerified = false;
        posthog.capture('user_membership_needs_review', {
          data: {
            matchedSignals,
            matchedCount,
            nameMatch: `Name Match: ${nameMatch}`,
            phoneMatch: `Name Match: ${phoneMatch}`,
            userId: this.userId,
          }
        });
        this.isNeedReview = true;
        await this.updateMembershipInProfile({
          statementVerified: false,
          statementAccessGranted: false,
          statementFlags: flags,
          statementMatchedSignals: matchedSignals,
          statementMatchedCount: matchedCount,
          statementNameMatch: nameMatch,
          statementPhoneMatch: phoneMatch,
          statementStatus: status,
          statementVerificationMode: this.membership?.statementRequiresHardReview ? 'hard' : 'soft',
          statementRequiresHardReview: this.membership?.statementRequiresHardReview === true,
        }, { incrementAttemptCount: true });
        return;
      }

      if (status === 'soft_verified') {
        this.isVerified = false;
        this.isSoftVerified = true;
        this.isNeedReview = false;
        await this.updateMembershipInProfile({
          statementVerified: false,
          statementAccessGranted: true,
          statementFlags: flags,
          statementMatchedSignals: matchedSignals,
          statementMatchedCount: matchedCount,
          statementNameMatch: nameMatch,
          statementPhoneMatch: phoneMatch,
          statementStatus: status,
          statementVerificationMode: 'soft',
          statementRequiresHardReview: false,
        }, { incrementAttemptCount: true });
        return;
      }

      // status === 'verified'
      this.isVerified = true;
      this.isSoftVerified = false;
      this.isNeedReview = false;

      await this.updateMembershipInProfile({
        statementVerified: true,
        statementAccessGranted: true,
        statementFlags: flags,
        statementMatchedSignals: matchedSignals,
        statementMatchedCount: matchedCount,
        statementNameMatch: nameMatch,
        statementPhoneMatch: phoneMatch,
        statementStatus: status,
        statementVerificationMode: 'hard',
        statementRequiresHardReview: false,
      }, { incrementAttemptCount: true });

    } catch (err) {
      console.error(err);
      this.error = 'Failed to verify document.';
    } finally {
      this.isProcessing = false;
    }

  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  startOver() {
    if (this.hasReachedAttemptLimit) {
      return;
    }
    this.file = null;
    this.error = null;
    this.result = null;
    this.isVerified = false;
    this.isSoftVerified = false;
    this.isNeedReview = false;
  }

  private async updateMembershipInProfile(updates: any, opts: { incrementAttemptCount?: boolean } = {}) {
    // load latest profile
    const profile = await this.userService.getUserProfile();
    const all = Array.isArray(profile.memberships) ? [...profile.memberships] : [];
    const idx = all.findIndex(m => m.id === this.membershipId);
    if (idx === -1) throw new Error('Membership not found');

    const membership = all[idx] || {};
    let attemptCount = Number(membership?.statementAttemptCount ?? 0);
    if (isNaN(attemptCount) || attemptCount < 0) {
      attemptCount = 0;
    }

    const finalUpdates = {
      ...updates,
      ...(opts.incrementAttemptCount ? { statementAttemptCount: attemptCount + 1 } : {}),
    };

    if (opts.incrementAttemptCount) {
      attemptCount += 1;
    }

    // merge updates into just that one
    all[idx] = {
      ...membership,
      ...finalUpdates
    };

    // write back only memberships array
    await this.userService.updateUserProfile({ memberships: all });

    this.membership = all[idx];
    this.syncAttemptState();
  }

  private syncAttemptState(membership?: any) {
    const source = membership ?? this.membership;
    const count = typeof source?.statementAttemptCount === 'number' && !isNaN(source.statementAttemptCount)
      ? source.statementAttemptCount
      : 0;
    this.attemptCount = count;
    this.hasReachedAttemptLimit = this.attemptCount >= this.attemptLimit;
  }

  get requiresHardReview(): boolean {
    return this.membership?.statementRequiresHardReview === true || this.membership?.statementStatus === 'hard_required';
  }

  get hardReviewMessage(): string {
    return this.membership?.statementReviewReason ||
      'Your membership document was flagged during review. Please submit a clearer document. This submission must be fully approved before you can continue.';
  }

  checkApprovalState() {
    if (this.membership.statementVerified) {
      this.isVerified = true;
      this.isSoftVerified = false;
      this.isNeedReview = false;
      this.result = {};
      return;
    }
    if (this.membership.statementAccessGranted) {
      this.isVerified = false;
      this.isSoftVerified = true;
      this.isNeedReview = false;
      this.result = {};
    }
  }

  onClickContinue() {
    if (this.isOnboarding && (this.isVerified || this.isSoftVerified)) {
      this.router.navigate(['/onboard/get-started']);
    } else {
      this.router.navigate(['/profile']);
    }
  }

  onClickBack() {
    this.router.navigate(['/onboard/membership']);
  }
}
